// @ts-nocheck
import { AlertTriangle, Lock } from 'lucide-react';
import { SHUTDOWN_MODES } from './voteRoomShutdown';

/**
 * Shared shutdown mode picker used in the setup wizard step and the live settings slide-over.
 */
export const ShutdownRuleForm = ({
  mode,
  customThreshold,
  hours,
  readOnly = false,
  onModeChange,
  onCustomThresholdChange,
  onHoursChange,
}) => {
  const showThresholdError =
    !readOnly && mode === 'threshold_custom' && (customThreshold < 76 || customThreshold > 100);
  const showHoursError = !readOnly && mode === 'time_based' && (hours < 1 || hours > 720);

  return (
    <div className="space-y-4">
      {readOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-800 flex items-start gap-2">
          <Lock size={13} className="mt-0.5 flex-shrink-0 text-amber-600" />
          <div>
            <span className="font-semibold">Shutdown Rule Locked.</span>{' '}
            <span>Change the rule by creating a new analysis and Vote Room.</span>
          </div>
        </div>
      )}

      {SHUTDOWN_MODES.map((opt) => {
        const selected = mode === opt.id;
        return (
          <label
            key={opt.id}
            className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-all ${
              selected ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-100'
            } ${readOnly ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:border-gray-200'}`}
            onClick={() => {
              if (!readOnly) onModeChange(opt.id);
            }}
          >
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                selected ? 'border-emerald-500' : 'border-gray-300'
              }`}
            >
              {selected && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
            </div>
            <div className="space-y-0.5">
              <p className="text-xs font-semibold text-gray-900">{opt.label}</p>
              {!readOnly && opt.id === 'threshold_75' && (
                <p className="text-[11px] text-gray-500">Closes as soon as 75% of eligible shares approve.</p>
              )}
              {!readOnly && opt.id === 'threshold_custom' && (
                <p className="text-[11px] text-gray-500">
                  Closes when approvals reach your chosen percentage above 75%.
                </p>
              )}
              {!readOnly && opt.id === 'time_based' && (
                <p className="text-[11px] text-gray-500">
                  Closes a fixed number of hours after launch, regardless of votes.
                </p>
              )}
            </div>
          </label>
        );
      })}

      {mode === 'threshold_custom' && (
        <div className="space-y-1 pl-7">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Approval Threshold</span>
            <input
              type="number"
              min="76"
              max="100"
              value={customThreshold}
              disabled={readOnly}
              onChange={(e) => onCustomThresholdChange(Math.min(100, Math.max(0, +e.target.value || 0)))}
              className="w-20 h-7 px-2 text-xs border border-gray-200 rounded-md outline-none focus:border-emerald-400 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
            />
            <span className="text-xs text-gray-500">% of eligible shares must approve</span>
          </div>
          {showThresholdError && (
            <p className="flex items-center gap-1 text-[11px] text-amber-700">
              <AlertTriangle size={11} className="flex-shrink-0" />
              Threshold must be between 76% and 100%.
            </p>
          )}
        </div>
      )}

      {mode === 'time_based' && (
        <div className="space-y-1 pl-7">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Duration</span>
            <input
              type="number"
              min="1"
              max="720"
              value={hours}
              disabled={readOnly}
              onChange={(e) => onHoursChange(Math.min(720, Math.max(0, +e.target.value || 0)))}
              className="w-20 h-7 px-2 text-xs border border-gray-200 rounded-md outline-none focus:border-emerald-400 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
            />
            <span className="text-xs text-gray-500">hours after launch</span>
          </div>
          {showHoursError && (
            <p className="flex items-center gap-1 text-[11px] text-amber-700">
              <AlertTriangle size={11} className="flex-shrink-0" />
              Duration must be between 1 and 720 hours.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
